import { Box, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DynamicTypography } from 'src/components/DynamicTextTypography'
import GenericTable from 'src/components/table/GenericTable'
import { IProjectStatus } from 'src/generated-types/project-status'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { GridColumns } from '@mui/x-data-grid-pro'
import { IProjectInfo } from 'src/generated-types/project'
import { AppRoutesNavigation } from 'src/app-routes'
import { projectStatusColors } from 'src/constant'

const ProjectsTable: React.FC<{ projects: IProjectInfo[] }> = ({ projects }) => {
  const { customerId } = useParams()
  const { t: translate } = useTranslation('projects')
  const theme = useTheme()
  const navigate = useNavigate()
  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'name',
      headerName: translate('project-name'),
      flex: 3,
      cellClassName: 'font-weight-semi-bold',
      sortable: false,
 renderCell: (params) => (
    <span style={{ textTransform: 'capitalize' }}>
      {params.value}
    </span>
  )
    },
    {
      field: 'locality',
      headerName: translate('locality', { ns: 'locality' }),
      flex: 3,
      cellClassName: 'font-weight-semi-bold',
      sortable: false,
      renderCell: params => <span style={{ textTransform: 'capitalize' }}>{params.row.locality.name}</span>,
    },
    {
      field: 'responsibleUser',
      headerName: translate('responsible-user', { ns: 'projects' }),
      flex: 3,
      cellClassName: 'font-weight-semi-bold',
      renderCell: params => {
        if (!params.row.customerContact) return <span className="font-bold">-</span>
        const fullName = getUserDetail(params.row.customerContact, UserDetail.FullName)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <DynamicTypography variant="subtitle2" fontWeight={700} text={fullName} />
            <DynamicTypography variant="body2" fontWeight={400} text={params.row.customerContact.email} />
          </div>
        )
      },
      sortable: false,
    },
    {
      field: 'contactNumber',
      headerName: translate('responsible-user-contact', { ns: 'projects' }),
      flex: 2,
      cellClassName: 'font-weight-semi-bold',
      renderCell: params => <span>{params.row.customerContact?.phoneNumber || '-'}</span>,
      sortable: false,
    },
    {
      field: 'status',
      headerName: translate('status', { ns: 'projects' }),
      flex: 2,
      cellClassName: 'font-weight-semi-bold',
      renderCell: params => (
        <Box
          sx={{
            backgroundColor: getThemeColor(theme, projectStatusColors[params.row.status]),
            color: getThemeColor(theme, MUIThemeColors.white),
            borderRadius: '4px',
            padding: '4px 8px',
          }}
        >
          {translate(IProjectStatus[params.row.status]) || '-'}
        </Box>
      ),
      sortable: false,
    },
    {
      field: 'ourContact',
      headerName: translate('our-references'),
      flex: 3,
      cellClassName: 'font-weight-semi-bold',
      renderCell: params => {
        const fullName = getUserDetail(params.row.ourContact, UserDetail.FullName)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <DynamicTypography variant="subtitle2" fontWeight={700} text={fullName} />
            <DynamicTypography variant="body2" fontWeight={400} text={params.row.ourContact.email} />
          </div>
        )
      },
      sortable: false,
    },
  ]

  return (
    <Box bgcolor={getThemeColor(theme, MUIThemeColors.white)} p={1}>
      <GenericTable
        data={projects}
        columns={STATIC_COLUMNS}
        id={`projects-table-${customerId}`}
        getRowHeight={() => 'auto'}
        onRowClick={({ row }) => navigate(AppRoutesNavigation.Project.project(row.customerId, row.localityId, row.id))}
      />
    </Box>
  )
}

export default ProjectsTable
